import { useMemo, useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import JSZip from "jszip";
import { useAuth } from "@/context/AuthContext";
import { scrub, createScrubContext, defaultOptions, isAcceptedFile, sanitizedFilename, ACCEPTED_EXT, CATEGORY_META } from "@/lib/scrubber";
import { buildKeyFile } from "@/lib/keyfile";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  WifiOff, Download, X, Plus, Users, LogOut, FileCode2,
  Upload, RotateCcw, Copy, Package, KeyRound,
} from "lucide-react";

const ACTION_LABEL = { replace: "Replace", remove: "Remove" };

function RuleRow({ id, meta, cfg, onToggle, onAction, replaceHint }) {
  return (
    <div className="border border-border bg-background px-3 py-2.5" data-testid={`rule-${id}`}>
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <span className="h-2 w-2 flex-shrink-0" style={{ background: meta.color }} />
          <span className="text-xs font-medium truncate">{meta.label}</span>
        </div>
        <Switch data-testid={`rule-${id}-toggle`} checked={cfg.enabled}
          onCheckedChange={(v) => onToggle(id, v)} className="data-[state=checked]:bg-primary rounded-none" />
      </div>
      {cfg.enabled && (
        <div className="mt-2 flex items-center gap-1">
          {["replace", "remove"].map((a) => (
            <button key={a} data-testid={`rule-${id}-action-${a}`} onClick={() => onAction(id, a)}
              className={`text-[10px] uppercase tracking-[0.15em] px-2 py-1 border transition-colors duration-100 ${
                cfg.action === a ? "border-primary text-primary bg-primary/10" : "border-border text-muted-foreground hover:text-foreground"
              }`}>
              {ACTION_LABEL[a]}
            </button>
          ))}
          {cfg.action === "replace" && replaceHint && (
            <span className="text-[10px] font-mono text-muted-foreground ml-auto truncate">{replaceHint}</span>
          )}
        </div>
      )}
    </div>
  );
}

function Pane({ title, lines, side }) {
  return (
    <div className="flex-1 flex flex-col min-w-0 border-border" data-testid={`pane-${side}`}>
      <div className="px-3 py-2 border-b border-border bg-surface">
        <span className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{title}</span>
      </div>
      <div className="flex-1 overflow-auto bg-background">
        <div className="font-mono text-xs leading-[1.7]">
          {lines.map((html, i) => (
            <div key={i} className="flex hover:bg-white/[0.03]">
              <span className="select-none text-right w-12 flex-shrink-0 pr-3 text-muted-foreground/50 border-r border-border">{i + 1}</span>
              <code className="px-3 whitespace-pre-wrap break-all flex-1" dangerouslySetInnerHTML={{ __html: html || "&nbsp;" }} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function Scrubber() {
  const { user, logout } = useAuth();
  const [files, setFiles] = useState([]); // [{name, text}]
  const [activeIdx, setActiveIdx] = useState(0);
  const [options, setOptions] = useState(defaultOptions());
  const [keyword, setKeyword] = useState("");
  const [dragging, setDragging] = useState(false);
  const [keyPass, setKeyPass] = useState("");
  const inputRef = useRef(null);

  const { results, mapping } = useMemo(() => {
    const ctx = createScrubContext();
    const res = files.map((f) => ({ name: f.name, ...scrub(f.text, options, ctx) }));
    const map = Array.from(ctx.mapping, ([token, info]) => ({ token, original: info.original, category: info.category }));
    return { results: res, mapping: map };
  }, [files, options]);
  const active = results[activeIdx];
  const grandTotal = results.reduce((s, r) => s + r.total, 0);

  const loadFiles = useCallback((fileList) => {
    const arr = Array.from(fileList || []);
    const accepted = arr.filter((f) => isAcceptedFile(f.name));
    const rejected = arr.length - accepted.length;
    if (rejected > 0) toast.error(`${rejected} file(s) skipped — accepted: ${ACCEPTED_EXT.join(", ")}`);
    if (accepted.length === 0) return;

    Promise.all(
      accepted.map(
        (file) =>
          new Promise((resolve) => {
            const reader = new FileReader();
            reader.onload = (e) => resolve({ name: file.name, text: String(e.target.result || "") });
            reader.readAsText(file);
          })
      )
    ).then((loaded) => {
      setFiles((prev) => {
        const merged = [...prev];
        loaded.forEach((nf) => {
          const idx = merged.findIndex((m) => m.name === nf.name);
          if (idx >= 0) merged[idx] = nf;
          else merged.push(nf);
        });
        return merged;
      });
      toast.success(`Loaded ${loaded.length} file(s) — scrubbed locally in your browser`);
    });
  }, []);

  const onDrop = (e) => { e.preventDefault(); setDragging(false); loadFiles(e.dataTransfer.files); };

  const toggleRule = (id, v) => setOptions((o) => ({ ...o, [id]: { ...o[id], enabled: v } }));
  const setAction = (id, a) => setOptions((o) => ({ ...o, [id]: { ...o[id], action: a } }));

  const addKeyword = () => {
    const k = keyword.trim();
    if (!k) return;
    setOptions((o) => (o.custom.keywords.includes(k) ? o : { ...o, custom: { ...o.custom, keywords: [...o.custom.keywords, k] } }));
    setKeyword("");
  };
  const removeKeyword = (k) =>
    setOptions((o) => ({ ...o, custom: { ...o.custom, keywords: o.custom.keywords.filter((x) => x !== k) } }));

  const triggerDownload = (blob, name) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = name;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  const downloadActive = () => {
    if (!active) return;
    triggerDownload(new Blob([active.scrubbed], { type: "text/plain;charset=utf-8" }), sanitizedFilename(active.name));
    toast.success("Sanitized file downloaded");
  };

  const downloadZip = async () => {
    if (!results.length) return;
    const zip = new JSZip();
    results.forEach((r) => zip.file(sanitizedFilename(r.name), r.scrubbed));
    const blob = await zip.generateAsync({ type: "blob" });
    triggerDownload(blob, "sanitized_configs.zip");
    toast.success(`Downloaded ${results.length} sanitized files as .zip`);
  };

  const downloadKey = async () => {
    if (!mapping.length) return;
    const content = await buildKeyFile(mapping, keyPass.trim() || undefined);
    triggerDownload(new Blob([content], { type: "application/json" }), "restore-key.sxmap");
    toast.success(keyPass.trim() ? "Encrypted restore key downloaded" : "Restore key downloaded");
  };

  const copyOut = async () => {
    if (!active) return;
    await navigator.clipboard.writeText(active.scrubbed);
    toast.success("Sanitized config copied");
  };

  const removeFile = (idx) => {
    setFiles((prev) => prev.filter((_, i) => i !== idx));
    setActiveIdx((i) => (idx <= i && i > 0 ? i - 1 : i));
  };

  const reset = () => { setFiles([]); setActiveIdx(0); };

  const replaceHints = { password: "\u27EASECRET_n\u27EB", username: "USER_n", publicIp: "169.254.x.x", email: "user_n@…", mac: "02:00:…", custom: "\u27EATERM_n\u27EB" };

  return (
    <div className="h-screen w-full flex flex-col md:flex-row overflow-hidden bg-background text-foreground">
      {/* Sidebar */}
      <aside className="w-full md:w-80 flex-shrink-0 border-r border-border bg-surface flex flex-col overflow-hidden">
        <div className="px-4 py-4 border-b border-border flex items-center gap-3 bg-black">
          <img src="/soverenx-logo.png" alt="SoverenX Systems" className="h-9 w-auto object-contain" data-testid="brand-logo" />
          <div className="min-w-0 ml-auto text-right">
            <p className="text-xs font-semibold tracking-tight leading-none">SoverenX Scrubber</p>
            <p className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mt-1 truncate">{user?.username} · {user?.role}</p>
          </div>
        </div>

        <div className="flex-1 overflow-auto p-4 space-y-5">
          <div>
            <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-2">Detection Rules</p>
            <div className="space-y-2">
              {Object.keys(CATEGORY_META).map((id) => (
                <RuleRow key={id} id={id} meta={CATEGORY_META[id]} cfg={options[id]} onToggle={toggleRule} onAction={setAction} replaceHint={replaceHints[id]} />
              ))}
            </div>
          </div>

          <div>
            <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-2">Custom Keywords · Customer / Location</p>
            <div className="flex gap-2">
              <Input data-testid="keyword-input" value={keyword} onChange={(e) => setKeyword(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && addKeyword()} placeholder="Acme Corp"
                className="font-mono text-xs rounded-none bg-background border-border h-9 focus-visible:ring-primary" />
              <Button data-testid="keyword-add-button" onClick={addKeyword} size="icon" className="rounded-none bg-primary text-primary-foreground hover:bg-accent-hover h-9 w-9 flex-shrink-0">
                <Plus className="h-4 w-4" />
              </Button>
            </div>
            {options.custom.keywords.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2" data-testid="keyword-list">
                {options.custom.keywords.map((k) => (
                  <span key={k} className="inline-flex items-center gap-1 text-[11px] font-mono px-2 py-0.5 border border-border bg-background">
                    {k}
                    <button data-testid={`keyword-remove-${k}`} onClick={() => removeKeyword(k)} className="text-muted-foreground hover:text-red-400"><X className="h-3 w-3" /></button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {active && (
            <div data-testid="summary-panel">
              <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-2">
                Change Summary {files.length > 1 && <span className="text-muted-foreground/60">· {active.name}</span>}
              </p>
              <div className="border border-border bg-background">
                <div className="flex items-center justify-between px-3 py-2 border-b border-border">
                  <span className="text-xs text-muted-foreground">Changes in file</span>
                  <span data-testid="summary-total" className="text-sm font-mono font-semibold text-primary">{active.total}</span>
                </div>
                {active.total === 0 ? (
                  <p className="px-3 py-2 text-xs text-muted-foreground">No sensitive data detected.</p>
                ) : (
                  Object.keys(CATEGORY_META).map((id) =>
                    active.summary[id] ? (
                      <div key={id} className="flex items-center justify-between px-3 py-1.5 text-xs" data-testid={`summary-${id}`}>
                        <span className="flex items-center gap-2 text-muted-foreground"><span className="h-2 w-2" style={{ background: CATEGORY_META[id].color }} />{CATEGORY_META[id].label}</span>
                        <span className="font-mono">{active.summary[id]}</span>
                      </div>
                    ) : null
                  )
                )}
                {files.length > 1 && (
                  <div className="flex items-center justify-between px-3 py-2 border-t border-border bg-surface">
                    <span className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">All {files.length} files</span>
                    <span data-testid="summary-grand-total" className="text-xs font-mono text-primary">{grandTotal}</span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {mapping.length > 0 && (
          <div className="border-t border-border p-3 space-y-2" data-testid="restore-key-section">
            <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Restore Key ({mapping.length})</p>
            <Input data-testid="key-passphrase-input" type="password" value={keyPass} onChange={(e) => setKeyPass(e.target.value)} placeholder="passphrase (optional)" className="font-mono text-xs rounded-none bg-background border-border h-9 focus-visible:ring-primary" />
            <Button data-testid="download-key-button" onClick={downloadKey} variant="outline" className="w-full rounded-none border-primary/50 text-primary hover:bg-primary/10 justify-start text-xs h-9"><KeyRound className="h-4 w-4 mr-2" /> Download Restore Key</Button>
            <p className="text-[9px] leading-relaxed text-muted-foreground/60">Keep this safe &amp; private — it's needed to put the secrets back. Never give it to the contractor.</p>
          </div>
        )}

        <div className="border-t border-border p-3 space-y-2">
          {user?.role === "admin" && (
            <Link to="/admin" data-testid="nav-admin-link">
              <Button variant="outline" className="w-full rounded-none border-border justify-start text-xs h-9"><Users className="h-4 w-4 mr-2" /> Manage Users</Button>
            </Link>
          )}
          <Link to="/restore" data-testid="nav-restore-link">
            <Button variant="outline" className="w-full rounded-none border-border justify-start text-xs h-9"><RotateCcw className="h-4 w-4 mr-2" /> Restore Secrets</Button>
          </Link>
          <Link to="/guide" data-testid="nav-guide-link">
            <Button variant="outline" className="w-full rounded-none border-border justify-start text-xs h-9"><FileCode2 className="h-4 w-4 mr-2" /> About &amp; Test Guide</Button>
          </Link>
          <Link to="/change-password" data-testid="nav-change-password-link">
            <Button variant="outline" className="w-full rounded-none border-border justify-start text-xs h-9"><KeyRound className="h-4 w-4 mr-2" /> Change Password</Button>
          </Link>
          <Button data-testid="logout-button" onClick={logout} variant="ghost" className="w-full rounded-none justify-start text-xs h-9 text-muted-foreground hover:text-red-400"><LogOut className="h-4 w-4 mr-2" /> Sign out</Button>
          <p className="text-[9px] leading-relaxed text-muted-foreground/60 pt-1" data-testid="ip-disclaimer">
            © {new Date().getFullYear()} SoverenX Systems. This tool, its name and logo are the intellectual property of SoverenX Systems. All rights reserved.
          </p>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 flex flex-col min-w-0 bg-background overflow-hidden">
        <header className="h-12 flex-shrink-0 border-b border-border flex items-center justify-between px-4 gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <FileCode2 className="h-4 w-4 text-muted-foreground flex-shrink-0" />
            <span data-testid="active-filename" className="text-xs font-mono truncate">{active ? active.name : "no file loaded"}</span>
            {files.length > 1 && <span className="text-[10px] text-muted-foreground flex-shrink-0">{activeIdx + 1}/{files.length}</span>}
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <span className="hidden sm:inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] text-primary border border-primary/40 px-2 py-1"><WifiOff className="h-3 w-3" /> 100% Local</span>
            {files.length > 0 && (
              <>
                <label className="cursor-pointer">
                  <input type="file" data-testid="add-more-input" multiple accept={ACCEPTED_EXT.join(",")} className="hidden" onChange={(e) => loadFiles(e.target.files)} />
                  <span className="inline-flex items-center rounded-none h-8 px-2 text-xs text-muted-foreground hover:text-foreground border border-transparent hover:border-border"><Plus className="h-3.5 w-3.5 mr-1" /> Add</span>
                </label>
                <Button data-testid="reset-button" onClick={reset} variant="ghost" size="sm" className="rounded-none h-8 text-xs text-muted-foreground"><RotateCcw className="h-3.5 w-3.5 mr-1.5" /> Reset</Button>
                <Button data-testid="copy-button" onClick={copyOut} variant="outline" size="sm" className="rounded-none h-8 text-xs border-border"><Copy className="h-3.5 w-3.5 mr-1.5" /> Copy</Button>
                {files.length > 1 && (
                  <Button data-testid="download-zip-button" onClick={downloadZip} size="sm" className="rounded-none h-8 text-xs bg-primary text-primary-foreground hover:bg-accent-hover font-medium"><Package className="h-3.5 w-3.5 mr-1.5" /> Download All (.zip)</Button>
                )}
                <Button data-testid="download-button" onClick={downloadActive} size="sm" variant={files.length > 1 ? "outline" : "default"}
                  className={files.length > 1 ? "rounded-none h-8 text-xs border-border" : "rounded-none h-8 text-xs bg-primary text-primary-foreground hover:bg-accent-hover font-medium"}>
                  <Download className="h-3.5 w-3.5 mr-1.5" /> {files.length > 1 ? "This File" : "Download Safe Config"}
                </Button>
              </>
            )}
          </div>
        </header>

        {files.length === 0 ? (
          <div className="flex-1 p-6 md:p-10 flex">
            <label onDragOver={(e) => { e.preventDefault(); setDragging(true); }} onDragLeave={() => setDragging(false)} onDrop={onDrop}
              className={`flex-1 flex flex-col items-center justify-center border-2 border-dashed cursor-pointer scanlines transition-colors duration-100 ${dragging ? "border-primary bg-primary/5" : "border-border hover:border-primary/60"}`} data-testid="dropzone">
              <input ref={inputRef} type="file" data-testid="file-input" multiple accept={ACCEPTED_EXT.join(",")} className="hidden" onChange={(e) => loadFiles(e.target.files)} />
              <Upload className="h-10 w-10 text-primary mb-6" strokeWidth={1.5} />
              <p className="font-mono text-sm tracking-wider text-foreground">[ DRAG CONFIG FILES HERE ]</p>
              <p className="font-mono text-xs text-muted-foreground mt-2">drop one or many · or click to browse</p>
              <p className="font-mono text-[11px] text-muted-foreground/70 mt-6">{ACCEPTED_EXT.join("  ·  ")}</p>
              <div className="mt-8 flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-primary/80"><WifiOff className="h-3.5 w-3.5" /> Nothing is ever uploaded to a server</div>
            </label>
          </div>
        ) : (
          <div className="flex-1 flex flex-col overflow-hidden">
            {files.length > 1 && (
              <div className="flex-shrink-0 border-b border-border bg-surface overflow-x-auto" data-testid="file-tabs">
                <div className="flex">
                  {results.map((r, i) => (
                    <div key={r.name} className={`group flex items-center gap-2 border-r border-border pl-3 pr-2 py-2 cursor-pointer flex-shrink-0 ${i === activeIdx ? "bg-background text-foreground" : "text-muted-foreground hover:text-foreground"}`}
                      data-testid={`file-tab-${i}`} onClick={() => setActiveIdx(i)}>
                      <span className="text-xs font-mono truncate max-w-[160px]">{r.name}</span>
                      <span className="text-[10px] font-mono text-primary">{r.total}</span>
                      <button data-testid={`file-remove-${i}`} onClick={(e) => { e.stopPropagation(); removeFile(i); }} className="text-muted-foreground/60 hover:text-red-400"><X className="h-3 w-3" /></button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className="flex-1 flex flex-col md:flex-row overflow-hidden divide-y md:divide-y-0 md:divide-x divide-border">
              <Pane title="Original" lines={active.origLines} side="original" />
              <Pane title="Sanitized" lines={active.scrubLines} side="sanitized" />
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
